/**
 * Route Tracking Service
 * Fetches confirmed routes and their execution status
 */

import { supabase, getProdSupabaseClient } from './client';
import { classifyRelease } from '@/lib/lifecycle/releaseDisposition';
import { logSystemError } from '@/lib/utils/errorTranslator';
import {
  RouteTrackerItem,
  RouteTrackerSummary,
  RouteTrackerFilters,
  RouteRecallDetail,
  ReleasedRecallDetail
} from '@/types/routeTracking';
import type {
  TechnicianRouteWithDetails,
  RouteData,
  RouteStop,
  ReleasedRecall
} from '@/types/technicianRoutes';

// Helper to determine route status
// Helper function removed as we're using 'active' status for confirmed routes

/**
 * Extract tenant_id from JWT token
 * Used to pass tenant_id explicitly to RPC functions
 */
const getTenantIdFromSession = async (): Promise<string | null> => {
  try {
    const {
      data: { session }
    } = await supabase.auth.getSession();
    if (!session?.access_token) return null;

    const payload = session.access_token.split('.')[1];
    if (!payload) return null;

    const claims = JSON.parse(atob(payload));
    return claims.tenant_id || null;
  } catch {
    return null;
  }
};

/**
 * Batch an array into chunks to avoid URL length limits in Supabase .in() queries.
 * Supabase encodes .in() values in the URL, which can exceed HTTP limits with many IDs.
 * @param array - The array to batch
 * @param batchSize - Maximum items per batch (default: 100, ~3.6KB for UUIDs)
 */
function batchArray<T>(array: T[], batchSize = 100): T[][] {
  const batches: T[][] = [];
  for (let i = 0; i < array.length; i += batchSize) {
    batches.push(array.slice(i, i + batchSize));
  }
  return batches;
}

// Helper to create technician initials
const getTechnicianInitials = (name: string | undefined): string => {
  if (!name) return '?';
  return (
    name
      .split(' ')
      .map((n: string) => n[0])
      .join('')
      .toUpperCase() || '?'
  );
};

// Interface for recall status updates

/** Customer-stop fields for the tracker: live assignment values win (notes can be edited). */
function customerStopDetail(
  live:
    | {
        stopKind?: 'business' | 'customer';
        contactId?: string | null;
        addressLine2?: string | null;
        dispatcherNote?: string | null;
      }
    | undefined,
  recall: RouteStop['recalls'][number],
  stop: RouteStop
): Pick<RouteRecallDetail, 'stopKind' | 'contactId' | 'addressLine2' | 'dispatcherNote'> {
  const stopKind = live?.stopKind || recall.stop_kind || stop.stop_kind;
  const contactId = live?.contactId || recall.contact_id || stop.contact_id;
  const addressLine2 = live?.addressLine2 || recall.address_line2 || stop.address_line2;
  const dispatcherNote = live?.dispatcherNote ?? recall.dispatcher_note;
  return {
    ...(stopKind && { stopKind }),
    ...(contactId && { contactId }),
    ...(addressLine2 && { addressLine2 }),
    ...(dispatcherNote && { dispatcherNote })
  };
}

/** Assignment columns read when a released manual stop goes back to the stops list. */
export interface ReleasedManualAssignment {
  vin: string;
  campaign_number: string | null;
  tenant_id: string;
  technician_id: string | null;
  dealer_name: string | null;
  dealer_address: string | null;
  dealer_city: string | null;
  dealer_region: string | null;
  dealer_postal_code: string | null;
  dealer_phone: string | null;
  manual_stop_lat: number | null;
  manual_stop_lng: number | null;
  manual_stop_reason: string | null;
  notes: string | null;
  vehicle_year: number | null;
  vehicle_make: string | null;
  vehicle_model: string | null;
  stop_kind?: string | null;
  contact_id?: string | null;
  address_line2?: string | null;
  dispatcher_note?: string | null;
}

/**
 * The selected_recalls row that returns a released manual stop to the stops list.
 * - 'update': the staged row still exists (assigning never deletes it). Refresh location,
 *   vehicle and notes only; its id, owner, household, note and address check stay. Leaving
 *   out stop_kind/contact_id/dispatcher_note also keeps trg_sr_customer_guard from rejecting
 *   a non-dispatcher when the note was edited on the assignment after staging.
 * - 'insert': the staged row was removed, so recreate it. address_validation can't come back
 *   (the assignment never carries it): the stop shows without an address check.
 */
export function buildReleasedStagedRow(
  a: ReleasedManualAssignment,
  mode: 'update' | 'insert',
  userId?: string
): Record<string, unknown> {
  // Same defaults the old upsert used: '' for required text, null for the rest.
  const text = (v: string | null) => v || '';
  const orNull = <T>(v: T | null | undefined) => v || null;
  const fields = {
    dealer_name: text(a.dealer_name),
    dealer_address: text(a.dealer_address),
    dealer_city: text(a.dealer_city),
    dealer_region: text(a.dealer_region),
    dealer_postal_code: orNull(a.dealer_postal_code),
    dealer_phone: orNull(a.dealer_phone),
    dealer_latitude: orNull(a.manual_stop_lat),
    dealer_longitude: orNull(a.manual_stop_lng),
    is_manual_stop: true,
    manual_stop_reason: text(a.manual_stop_reason),
    manual_stop_notes: orNull(a.notes),
    vehicle_year: orNull(a.vehicle_year),
    vehicle_make: orNull(a.vehicle_make),
    vehicle_model: orNull(a.vehicle_model),
    car_year: orNull(a.vehicle_year),
    make_name: orNull(a.vehicle_make),
    model_name: orNull(a.vehicle_model)
  };
  if (mode === 'update') return fields;

  return {
    id: crypto.randomUUID(),
    user_id: userId,
    tenant_id: a.tenant_id,
    vin: a.vin,
    campaign_number: a.campaign_number,
    ...fields,
    // Keep a released customer stop a customer stop (same household, unit and note).
    ...(a.stop_kind === 'customer' && {
      stop_kind: 'customer',
      contact_id: a.contact_id,
      address_line2: orNull(a.address_line2),
      dispatcher_note: orNull(a.dispatcher_note)
    })
  };
}

/**
 * Manual reason and customer-stop ids for route_data.released_recalls, so the tracker can
 * still show a released stop as a customer stop. Ids only: route_data never holds names,
 * phones or emails (a DB trigger rejects them).
 */
export function releasedStopFields(
  a: Pick<
    ReleasedManualAssignment,
    'manual_stop_reason' | 'stop_kind' | 'contact_id' | 'address_line2' | 'dispatcher_note'
  >
): Partial<ReleasedRecall> {
  return {
    ...(a.manual_stop_reason && { manual_stop_reason: a.manual_stop_reason }),
    ...(a.stop_kind === 'customer' && {
      stop_kind: 'customer' as const,
      contact_id: a.contact_id ?? null,
      address_line2: a.address_line2 ?? null
    }),
    ...(a.dispatcher_note && { dispatcher_note: a.dispatcher_note })
  };
}

const STOP_KEPT = 'It stays with the technician.';

function manualStopReturnError(error: { code?: string }): Error {
  logSystemError(error, 'returning a manual stop to the stops list');
  return new Error(
    error.code === 'PT401'
      ? `Only a dispatcher can return a customer stop to the stops list. ${STOP_KEPT}`
      : `This stop couldn't be returned to the stops list. ${STOP_KEPT} Please try again.`
  );
}

/** Returns a released manual stop to the stops list; throws so the caller keeps the assignment. */
async function returnManualStopToStaging(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  prodClient: any,
  a: ReleasedManualAssignment
): Promise<void> {
  const lookup = prodClient
    .from('selected_recalls')
    .select('id')
    .eq('tenant_id', a.tenant_id)
    .eq('vin', a.vin);
  const { data: staged, error: lookupError } = await (
    a.campaign_number === null
      ? lookup.is('campaign_number', null)
      : lookup.eq('campaign_number', a.campaign_number)
  ).maybeSingle();
  if (lookupError) throw manualStopReturnError(lookupError);

  if (staged) {
    const { error } = await prodClient
      .from('selected_recalls')
      .update(buildReleasedStagedRow(a, 'update'))
      .eq('id', staged.id);
    if (error) throw manualStopReturnError(error);
    return;
  }

  // The user marking it released owns the recreated row.
  const {
    data: { session }
  } = await supabase.auth.getSession();
  const userId = session?.user?.id || a.technician_id || undefined;
  const { error } = await prodClient
    .from('selected_recalls')
    .insert(buildReleasedStagedRow(a, 'insert', userId));
  if (error) throw manualStopReturnError(error);
}

export interface RecallStatusUpdate {
  assignmentId: string;
  status: 'completed' | 'vehicle_sold' | 'not_available' | 'incomplete' | 'other';
  reason?: string | undefined;
  notes?: string | undefined;
  servicedByTechnicianId?: string | undefined;
  servicedByTechnicianName?: string | undefined;
  isPreferred?: boolean | undefined;
  completedByUserId?: string | undefined;
  completedByUserName?: string | undefined;
}

export const routeTrackingAPI = {
  /**
   * Get summary metrics for the route tracker dashboard
   */
  // eslint-disable-next-line max-lines-per-function
  async getSummary(): Promise<RouteTrackerSummary> {
    try {
      const today = new Date().toISOString().split('T')[0];
      if (!today) {
        return {
          activeRoutesToday: 0,
          recallsCompletedToday: 0,
          completionRate: 0
        };
      }

      // Get active routes for today based on expected_visit_date
      // Group by technician to count unique routes
      const prodClient = getProdSupabaseClient();
      const { data: activeRoutes, error: routesError } = await prodClient
        .from('technician_assignments')
        .select('technician_id, expected_visit_date')
        .in('assignment_status', ['assigned', 'confirmed', 'in_progress'])
        .eq('expected_visit_date', today as any)
        .not('expected_visit_date', 'is', null as any);

      if (routesError) throw routesError;

      // Count unique technician-date combinations (using expected_visit_date)
      const uniqueRoutes = new Set(
        (activeRoutes as any[])?.map((r: any) => `${r.technician_id}-${r.expected_visit_date}`) ||
          []
      );

      // Get recalls completed today
      const { data: completedToday, error: completedError } = await prodClient
        .from('technician_assignments')
        .select('id')
        .eq('assignment_status', 'completed')
        .gte('completed_at', `${today}T00:00:00`)
        .lte('completed_at', `${today}T23:59:59`);

      if (completedError) throw completedError;

      // Get total recalls scheduled for today (by expected_visit_date) for completion rate
      const { data: assignedToday, error: assignedError } = await prodClient
        .from('technician_assignments')
        .select('id')
        .eq('expected_visit_date', today as any)
        .not('expected_visit_date', 'is', null as any);

      if (assignedError) throw assignedError;

      const completionRate =
        assignedToday && assignedToday.length > 0
          ? ((completedToday?.length || 0) / assignedToday.length) * 100
          : 0;

      return {
        activeRoutesToday: uniqueRoutes.size,
        recallsCompletedToday: completedToday?.length || 0,
        completionRate: Math.round(completionRate * 10) / 10 // Round to 1 decimal
      };
    } catch {
      return {
        activeRoutesToday: 0,
        recallsCompletedToday: 0,
        completionRate: 0
      };
    }
  },

  /**
   * Get routes within a date range with their execution details
   * Includes all assigned recalls (assigned, confirmed, in_progress) so users can update statuses
   */
  // eslint-disable-next-line max-lines-per-function
  async getRoutes(filters: RouteTrackerFilters): Promise<RouteTrackerItem[]> {
    try {
      // Get all assigned recalls within date range based on expected_visit_date
      // This allows users to see routes by scheduled service date, not assignment date
      const prodClient = getProdSupabaseClient();
      const { data: assignedRecalls, error: assignmentsError } = await prodClient
        .from('technician_assignments')
        .select('*, notes')
        .gte('expected_visit_date', filters.dateRange.start)
        .lte('expected_visit_date', filters.dateRange.end)
        .not('expected_visit_date', 'is', null as any) // Only include assignments with scheduled dates
        .order('expected_visit_date', { ascending: false });

      if (assignmentsError) {
        throw assignmentsError;
      }

      if (!assignedRecalls || assignedRecalls.length === 0) {
        return [];
      }

      // Type assertion for assignments
      type AssignmentWithDate = (typeof assignedRecalls)[0] & {
        technician_id: string;
        expected_visit_date: string;
      };

      // Group assignments by technician and expected_visit_date to reconstruct routes
      const routeGroups = new Map<string, AssignmentWithDate[]>();

      assignedRecalls.forEach((assignment: any) => {
        if (!assignment.technician_id || !assignment.expected_visit_date) {
          return;
        }
        // Use expected_visit_date for grouping (the scheduled service date)
        const date = assignment.expected_visit_date.split('T')[0];
        const key = `${assignment.technician_id}-${date}`;

        if (!routeGroups.has(key)) {
          routeGroups.set(key, []);
        }
        routeGroups.get(key)!.push(assignment as AssignmentWithDate);
      });

      // Get technician details - filter out null technician_ids
      const technicianIds = [
        ...new Set(
          assignedRecalls
            .map((a: any) => a.technician_id)
            .filter((id: any) => id !== null && id !== undefined)
        )
      ];

      // First, try to get basic technician info
      const { data: technicians, error: techError } = await prodClient
        .from('technicians')
        .select('id, full_name')
        .in('id', technicianIds);

      if (techError) {
        throw techError;
      }

      // Create a map of technician data
      const techMap = new Map((technicians as any[])?.map((t: any) => [t.id, t]) || []);

      // Create route items from grouped assignments

      const routeItems: RouteTrackerItem[] = Array.from(routeGroups.entries())
        .map(([key, assignments]) => {
          // Fix key splitting - technician ID has hyphens, and date has hyphens too
          // Key format: "technician-id-2025-10-08"
          // We need to find the last occurrence of "-YYYY-MM-DD" pattern
          const dateMatch = key.match(/-\d{4}-\d{2}-\d{2}$/);
          if (!dateMatch) {
            return null;
          }

          const technicianId = key.substring(0, dateMatch.index ?? 0);
          const routeDate = key.substring((dateMatch.index ?? 0) + 1); // Remove the leading dash
          const firstAssignment = assignments[0];

          const recalls: RouteRecallDetail[] = assignments.map((a: any) => {
            // Build dealer address from available fields
            const addressParts: string[] = [];
            if (a.dealer_street) {
              addressParts.push(a.dealer_street);
            }
            if (a.dealer_city) addressParts.push(a.dealer_city);
            if (a.dealer_region) addressParts.push(a.dealer_region);
            const dealerAddress = addressParts.length > 0 ? addressParts.join(', ') : undefined;

            // Determine the actual status - if cancelled, check notes for original status
            const actualStatus: 'assigned' | 'in_progress' | 'completed' | 'cancelled' =
              (a.assignment_status as 'assigned' | 'in_progress' | 'completed' | 'cancelled') ||
              'assigned';

            return {
              assignmentId: a.id,
              vin: a.vin,
              vehicleYear: a.vehicle_year || null, // Use null instead of 0 for missing data
              vehicleMake: a.vehicle_make || '',
              vehicleModel: a.vehicle_model || '',
              campaignNumber: a.campaign_number,
              recallTitle: a.recall_title || '',
              dealerName: a.dealer_name,
              dealerCity: a.dealer_city || '',
              dealerRegion: a.dealer_region || '',
              dealerAddress: dealerAddress || '',
              status: actualStatus,
              assignedAt: a.assigned_at,
              completedAt: a.completed_at || undefined,
              // Store notes for status restoration
              notes: a.notes || undefined
            };
          });

          const dealers = new Set(recalls.map(r => r.dealerName)).size;
          const campaigns = [...new Set(recalls.map(r => r.campaignNumber))];

          const tech = techMap.get(technicianId);
          const initials = getTechnicianInitials((tech as any)?.full_name);

          // For now, use the most common dealer region as the route location
          // MSA integration will be added in a future update
          // (primaryRegion calculation removed as it's not currently used)

          // Calculate status counts and route status
          const completedRecalls = recalls.filter(r => r.status === 'completed').length;
          const inProgressRecalls = recalls.filter(r => r.status === 'in_progress').length;
          const pendingRecalls = recalls.length - completedRecalls - inProgressRecalls;

          // Determine overall route status
          let routeStatus: 'active' | 'completed' | 'in_progress' | 'delayed' = 'active';
          if (completedRecalls === recalls.length) {
            routeStatus = 'completed';
          } else if (inProgressRecalls > 0 || completedRecalls > 0) {
            routeStatus = 'in_progress';
          }

          if (!firstAssignment) {
            return null;
          }

          return {
            routeId: key,
            routeNumber: (tech as any)?.full_name || 'Unknown',
            confirmedAt: (firstAssignment as any).assigned_at,
            routeDate: routeDate, // This is now the expected_visit_date, not assigned_at
            technicianId: technicianId,
            technicianName: (tech as any)?.full_name || 'Unknown',
            technicianInitials: initials,
            totalRecalls: recalls.length,
            completedRecalls,
            inProgressRecalls,
            pendingRecalls,
            dealerCount: dealers,
            campaigns,
            status: routeStatus,
            recalls,
            // Default to 1 for assignment-based route building
            routeSequence: 1
          };
        })
        .filter(item => item !== null); // Filter out null items

      // Apply additional filters
      let filtered = routeItems;

      if (filters.status && filters.status !== 'all') {
        filtered = filtered.filter(item => item.status === filters.status);
      }

      if (filters.technicianId) {
        filtered = filtered.filter(item => item.technicianId === filters.technicianId);
      }

      if (filters.searchTerm) {
        const term = filters.searchTerm.toLowerCase();
        filtered = filtered.filter(
          item =>
            item.routeNumber.toLowerCase().includes(term) ||
            item.technicianName.toLowerCase().includes(term) ||
            item.recalls.some(r => r.vin.toLowerCase().includes(term))
        );
      }

      return filtered;
    } catch {
      return [];
    }
  },

  /**
   * Get list of technicians for filtering
   */
  async getTechnicians(): Promise<Array<{ id: string; name: string }>> {
    try {
      const prodClient = getProdSupabaseClient();
      const { data, error } = await prodClient
        .from('technicians')
        .select('id, full_name')
        .eq('is_active', true)
        .order('full_name');

      if (error) throw error;

      return (data as any[])?.map((t: any) => ({ id: t.id, name: t.full_name })) || [];
    } catch {
      return [];
    }
  },

  /**
   * Update multiple recall statuses in batch
   */
  // eslint-disable-next-line max-lines-per-function
  async updateRecallStatuses(
    updates: RecallStatusUpdate[]
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Process each update
      const updatePromises = updates.map(async update => {
        // Does this release return the recall to the pool, or end it permanently?
        //
        // This used to be a case-sensitive `includes()` against a list declared right
        // here, which meant the app's Title Case spellings ('Parts Not Available') and
        // its note-appended strings ('Parts Not Available: need parts') matched nothing
        // and were permanently cancelled instead of recycled. classifyRelease normalises
        // both, so every app build already in the field is repaired without an app
        // release. See src/lib/lifecycle/releaseDisposition.ts for the rule.
        const classification = classifyRelease(update.status, update.reason);
        const shouldAutoUnassign = classification.disposition === 'recycle';

        if (classification.unrecognisedReason) {
          // Free text we do not recognise, closed out conservatively. Worth seeing: it is
          // either a new label a client started sending, or a technician typing prose
          // into a field that decides whether work comes back.
          logSystemError(
            new Error(`Unrecognised release reason: ${String(update.reason)}`),
            'classifying a recall release'
          );
        }

        const prodClient = getProdSupabaseClient();

        // For auto-unassign, save the recall history to route JSONB before deletion
        if (shouldAutoUnassign) {
          // 1. Fetch the assignment details before deletion (include manual stop fields)
          const { data: assignment, error: fetchError } = await prodClient
            .from('technician_assignments')
            .select(
              `id, vin, campaign_number, vehicle_year, vehicle_make, vehicle_model,
               dealer_name, dealer_city, dealer_region, dealer_address, dealer_postal_code, dealer_phone,
               technician_id, route_id, tenant_id,
               stop_type, manual_stop_reason, manual_stop_lat, manual_stop_lng, notes,
               stop_kind, contact_id, address_line2, dispatcher_note`
            )
            .eq('id', update.assignmentId)
            .single();

          // Without the row we can't tell a manual stop (which must go back to the stops
          // list) from a recall, so don't delete what we couldn't read.
          if (fetchError) {
            logSystemError(fetchError, 'reading an assignment to release');
            throw new Error("This stop couldn't be released. Please try again.");
          }

          // 2. Manual stops go back to the stops list FIRST: if that fails, the error
          // surfaces and the assignment stays, so the stop is never lost.
          if (assignment?.stop_type === 'manual' && assignment?.tenant_id) {
            await returnManualStopToStaging(
              prodClient,
              assignment as unknown as ReleasedManualAssignment
            );
          }

          // 3. Save released recall info to route's JSONB (if route exists)
          if (assignment?.route_id) {
            try {
              const { saveReleasedRecallToRoute } = await import('./technicianRoutes');

              // Get technician name if available
              let technicianName: string | undefined;
              if (assignment.technician_id) {
                const { data: tech } = await prodClient
                  .from('technicians')
                  .select('full_name')
                  .eq('id', assignment.technician_id)
                  .single();
                technicianName = tech?.full_name;
              }

              await saveReleasedRecallToRoute(assignment.route_id, {
                assignment_id: assignment.id,
                vin: assignment.vin,
                campaign_number: assignment.campaign_number,
                vehicle_year: assignment.vehicle_year,
                vehicle_make: assignment.vehicle_make || '',
                vehicle_model: assignment.vehicle_model || '',
                dealer_name: assignment.dealer_name || '',
                dealer_city: assignment.dealer_city,
                dealer_region: assignment.dealer_region,
                release_status:
                  update.status === 'incomplete'
                    ? 'incomplete'
                    : update.status === 'not_available'
                      ? 'not_available'
                      : 'other',
                release_reason: update.reason || update.status,
                released_at: new Date().toISOString(),
                technician_id: assignment.technician_id,
                technician_name: technicianName,
                stop_type: (assignment.stop_type as 'recall' | 'manual' | undefined) ?? 'recall',
                ...releasedStopFields(assignment as unknown as ReleasedManualAssignment)
              });
            } catch (saveError) {
              // Log but don't fail - the release should still happen
              // eslint-disable-next-line no-console
              console.error('Error saving released recall to route:', saveError);
            }
          }

          // 4. Update lifecycle record BEFORE deletion (while assignment exists)
          try {
            const { updateLifecycleOnCompletion } = await import('./recallLifecycle');
            await updateLifecycleOnCompletion(update.assignmentId, {
              finalStatus: update.status,
              shouldAutoUnassign: true,
              ...(update.reason && { otherReason: update.reason }),
              ...(update.servicedByTechnicianId && {
                servicedByTechnicianId: update.servicedByTechnicianId
              }),
              ...(update.servicedByTechnicianName && {
                servicedByTechnicianName: update.servicedByTechnicianName
              }),
              ...(update.isPreferred !== undefined && { isPreferred: update.isPreferred })
            });
          } catch (lifecycleError) {
            // Log error but don't fail the update
            // eslint-disable-next-line no-console
            console.error('Error updating lifecycle record:', lifecycleError);
          }

          // 5. Delete the assignment so the recall goes back to the available pool
          // (manual stops were already returned to the stops list in step 2)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { error: deleteError } = await (prodClient as any)
            .from('technician_assignments')
            .delete()
            .eq('id', update.assignmentId);

          if (deleteError) throw deleteError;
        } else {
          // Normal status update (close-out: completed, vehicle_sold, or close-out other reasons)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const updateData: any = {
            assignment_status:
              update.status === 'vehicle_sold' || update.status === 'other'
                ? 'cancelled'
                : update.status
          };

          // Add completion timestamp if marking as completed
          if (update.status === 'completed') {
            updateData.completed_at = new Date().toISOString();
          }

          // Store release status in proper columns for non-completed statuses
          if (update.status === 'vehicle_sold' || update.status === 'other') {
            updateData.release_status = update.status === 'vehicle_sold' ? 'vehicle_sold' : 'other';
            updateData.release_reason = update.reason || null;
          }

          // Store notes if provided
          if (update.notes) {
            updateData.notes = update.notes;
          }

          // Update serviced_by if different technician handled it
          if (update.servicedByTechnicianId) {
            updateData.technician_id = update.servicedByTechnicianId;
          }

          // Track who completed/updated the status
          if (update.completedByUserId) {
            updateData.completed_by_user_id = update.completedByUserId;
          }
          if (update.completedByUserName) {
            updateData.completed_by_user_name = update.completedByUserName;
          }

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { error: assignmentError } = await (prodClient as any)
            .from('technician_assignments')
            .update({
              ...updateData,
              updated_at: new Date().toISOString()
            })
            .eq('id', update.assignmentId);

          if (assignmentError) throw assignmentError;
        }

        // Update lifecycle record for NON-auto-unassign status changes only
        // (auto-unassign is handled above, before deletion)
        if (!shouldAutoUnassign) {
          try {
            const { updateLifecycleOnCompletion } = await import('./recallLifecycle');
            await updateLifecycleOnCompletion(update.assignmentId, {
              finalStatus: update.status,
              shouldAutoUnassign: false,
              ...(update.reason && { otherReason: update.reason }),
              ...(update.servicedByTechnicianId && {
                servicedByTechnicianId: update.servicedByTechnicianId
              }),
              ...(update.servicedByTechnicianName && {
                servicedByTechnicianName: update.servicedByTechnicianName
              }),
              ...(update.isPreferred !== undefined && { isPreferred: update.isPreferred })
            });
          } catch (lifecycleError) {
            // Log error but don't fail the update
            // eslint-disable-next-line no-console
            console.error('Error updating lifecycle record:', lifecycleError);
          }
        }
      });

      await Promise.all(updatePromises);

      // Note: Route auto-completion is handled by RouteTrackerPageV3's autoTransitionToCompleted
      // after the UI reloads fresh data from the database

      return { success: true };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error updating recall statuses:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update recall statuses'
      };
    }
  },

  /**
   * Get recall tracking metadata for enhanced details
   * Note: Metadata is now stored directly in technician_assignments table
   */
  async getRecallTrackingMetadata(assignmentIds: string[]): Promise<Map<string, any>> {
    try {
      const prodClient = getProdSupabaseClient();
      const metadataMap = new Map<string, unknown>();

      if (assignmentIds.length === 0) return metadataMap;

      // Batch IDs to avoid URL length limits (Supabase .in() encodes all IDs in URL)
      const batches = batchArray(assignmentIds, 100);

      // Fetch all batches in parallel for same effective latency
      const batchResults = await Promise.all(
        batches.map(batch =>
          prodClient
            .from('technician_assignments')
            .select('id, notes, assignment_status, completed_at')
            .in('id', batch)
        )
      );

      // Combine results from all batches
      for (const { data, error } of batchResults) {
        if (error) throw error;
        (data as any[])?.forEach((item: any) => {
          metadataMap.set(item.id, {
            assignment_id: item.id,
            reason: item.notes || undefined,
            final_status: item.assignment_status,
            completed_at: item.completed_at
          });
        });
      }

      return metadataMap;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching recall tracking metadata:', error);
      return new Map();
    }
  },

  /**
   * Get routes from the technician_routes table (new persistent storage)
   * This provides pre-computed route data with stops and recalls stored as JSONB
   */
  async getRoutesFromTable(filters: RouteTrackerFilters): Promise<RouteTrackerItem[]> {
    try {
      // Get tenant_id from session to pass explicitly to RPC
      const tenantId = await getTenantIdFromSession();

      // Use main supabase client for RPC calls (public schema functions)
      // RPC functions are in public schema, prod client would look in prod schema
      const { data: routes, error } = await supabase.rpc('get_routes_by_date_range', {
        p_start_date: filters.dateRange.start,
        p_end_date: filters.dateRange.end,
        p_technician_id: filters.technicianId || null,
        p_status: filters.status === 'all' ? null : filters.status || null,
        p_tenant_id: tenantId
      });

      if (error) {
        console.error('[RouteTracker] Error fetching routes from table:', error);
        // Fall back to assignment-based approach
        return this.getRoutes(filters);
      }

      if (!routes || routes.length === 0) {
        return [];
      }

      // Collect all assignment IDs to fetch current statuses
      const allAssignmentIds: string[] = [];
      (routes as TechnicianRouteWithDetails[]).forEach(route => {
        const routeData = route.route_data as RouteData;
        const stops = routeData?.stops || [];
        stops.forEach((stop: RouteStop) => {
          (stop.recalls || []).forEach(recall => {
            if (recall.assignment_id) {
              allAssignmentIds.push(recall.assignment_id);
            }
          });
        });
      });

      // Fetch current assignment statuses from technician_assignments table
      // This is the source of truth for recall statuses
      const currentStatuses = new Map<
        string,
        {
          status: string;
          completedAt?: string;
          notes?: string;
          completedByUserId?: string;
          completedByUserName?: string;
          releaseStatus?: string;
          releaseReason?: string;
          stopType?: 'recall' | 'manual';
          manualStopReason?: string;
          stopKind?: 'business' | 'customer';
          contactId?: string | null;
          addressLine2?: string | null;
          dispatcherNote?: string | null;
        }
      >();
      if (allAssignmentIds.length > 0) {
        const prodClient = getProdSupabaseClient();
        // Batch IDs to avoid URL length limits (Supabase .in() encodes all IDs in URL)
        const batches = batchArray(allAssignmentIds, 100);

        // Fetch all batches in parallel for same effective latency
        const batchResults = await Promise.all(
          batches.map(batch =>
            prodClient
              .from('technician_assignments')
              .select(
                'id, assignment_status, completed_at, notes, completed_by_user_id, completed_by_user_name, release_status, release_reason, stop_type, manual_stop_reason, stop_kind, contact_id, address_line2, dispatcher_note'
              )
              .in('id', batch)
          )
        );

        // Combine results from all batches
        for (const { data: assignments, error: assignmentError } of batchResults) {
          if (assignmentError) {
            // eslint-disable-next-line no-console
            console.error('[RouteTracker] Error fetching assignment statuses:', assignmentError);
            continue;
          }
          if (assignments) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (assignments as any[]).forEach((a: any) => {
              currentStatuses.set(a.id, {
                status: a.assignment_status || 'pending',
                completedAt: a.completed_at,
                notes: a.notes,
                completedByUserId: a.completed_by_user_id,
                completedByUserName: a.completed_by_user_name,
                releaseStatus: a.release_status,
                releaseReason: a.release_reason,
                stopType: a.stop_type,
                manualStopReason: a.manual_stop_reason,
                stopKind: a.stop_kind,
                contactId: a.contact_id,
                addressLine2: a.address_line2,
                dispatcherNote: a.dispatcher_note
              });
            });
          }
        }
      }

      // Transform stored routes to RouteTrackerItem format
      const routeItems: RouteTrackerItem[] = (routes as TechnicianRouteWithDetails[]).map(route => {
        const routeData = route.route_data as RouteData;
        const stops = routeData?.stops || [];

        // Flatten recalls from all stops, enriching with current status from assignments table
        // For completed routes, show all recalls from stored data even if assignments were deleted
        // For active routes, filter out recalls whose assignments were deleted (auto-unassigned)
        const recalls: RouteRecallDetail[] = stops.flatMap((stop: RouteStop) =>
          (stop.recalls || [])
            // Completed routes show all recalls from stored data; active routes require live assignments
            .filter(
              recall => route.status === 'completed' || currentStatuses.has(recall.assignment_id)
            )
            .map(recall => {
              // Get current status from assignments table if available (source of truth for active routes)
              const currentAssignment = currentStatuses.get(recall.assignment_id);
              // For completed routes without assignment record, use 'completed' status
              const currentStatus =
                currentAssignment?.status ||
                (route.status === 'completed' ? 'completed' : 'pending');

              return {
                assignmentId: recall.assignment_id,
                vin: recall.vin,
                vehicleYear: recall.vehicle_year,
                vehicleMake: recall.vehicle_make,
                vehicleModel: recall.vehicle_model,
                campaignNumber: recall.campaign_number,
                recallTitle: recall.recall_title,
                dealerName: stop.dealer_name,
                dealerCity: stop.dealer_city,
                dealerRegion: stop.dealer_region,
                dealerAddress: stop.dealer_address,
                dealerPostalCode: stop.dealer_postal_code,
                status: currentStatus,
                assignedAt: route.created_at,
                completedAt: currentAssignment?.completedAt || route.completed_at || undefined,
                notes: currentAssignment?.notes,
                completedByUserName: currentAssignment?.completedByUserName,
                releaseStatus: currentAssignment?.releaseStatus as
                  | 'not_there'
                  | 'not_available'
                  | 'incomplete'
                  | 'vehicle_sold'
                  | 'other'
                  | undefined,
                releaseReason: currentAssignment?.releaseReason,
                // Manual stop support - prefer JSONB data, fallback to assignment
                stopType: recall.stop_type || currentAssignment?.stopType,
                manualStopReason: recall.manual_stop_reason || currentAssignment?.manualStopReason,
                ...customerStopDetail(currentAssignment, recall, stop)
              };
            })
        );

        // Calculate status counts
        const completedRecalls = recalls.filter(r => r.status === 'completed').length;
        const inProgressRecalls = recalls.filter(r => r.status === 'in_progress').length;
        const pendingRecalls = recalls.length - completedRecalls - inProgressRecalls;

        // Map route status to RouteTrackerItem status
        // Route statuses: draft → confirmed → in_progress → completed/cancelled
        // RouteTracker statuses: active, in_progress, completed, delayed
        let routeStatus: 'active' | 'completed' | 'in_progress' | 'delayed' = 'active';
        if (route.status === 'completed') {
          routeStatus = 'completed';
        } else if (
          route.status === 'in_progress' ||
          inProgressRecalls > 0 ||
          completedRecalls > 0
        ) {
          routeStatus = 'in_progress';
        } else if (route.status === 'confirmed' || route.status === 'draft') {
          // Both draft and confirmed routes show as 'active' (pending work)
          routeStatus = 'active';
        }

        const campaigns = [...new Set(recalls.map(r => r.campaignNumber))];

        // Get released recalls from route_data JSONB (history of recalls released for reassignment)
        const releasedRecalls: ReleasedRecallDetail[] = (
          (routeData.released_recalls as ReleasedRecall[]) || []
        ).map(r => ({
          assignmentId: r.assignment_id,
          vin: r.vin,
          vehicleYear: r.vehicle_year,
          vehicleMake: r.vehicle_make,
          vehicleModel: r.vehicle_model,
          campaignNumber: r.campaign_number,
          dealerName: r.dealer_name,
          dealerCity: r.dealer_city,
          dealerRegion: r.dealer_region,
          releaseStatus: r.release_status,
          releaseReason: r.release_reason,
          releasedAt: r.released_at,
          technicianId: r.technician_id,
          technicianName: r.technician_name,
          stopType: r.stop_type,
          ...(r.manual_stop_reason && { manualStopReason: r.manual_stop_reason }),
          ...(r.stop_kind && { stopKind: r.stop_kind }),
          ...(r.contact_id && { contactId: r.contact_id }),
          ...(r.address_line2 && { addressLine2: r.address_line2 }),
          ...(r.dispatcher_note && { dispatcherNote: r.dispatcher_note })
        }));

        return {
          routeId: route.id,
          routeNumber: route.technician_name || 'Unknown',
          confirmedAt: route.confirmed_at || route.created_at,
          routeDate: route.route_date,
          technicianId: route.technician_id,
          technicianName: route.technician_name || 'Unknown',
          technicianInitials: getTechnicianInitials(route.technician_name),
          // Use actual count since deleted (auto-unassigned) recalls are filtered out
          totalRecalls: recalls.length,
          completedRecalls: completedRecalls,
          inProgressRecalls,
          pendingRecalls,
          dealerCount: route.total_stops || stops.length,
          campaigns,
          status: routeStatus,
          recalls,
          // Released recalls - history of recalls released for reassignment
          releasedRecalls: releasedRecalls.length > 0 ? releasedRecalls : undefined,
          // Route sequence for multiple routes per technician per day
          routeSequence: route.route_sequence || 1
        };
      });

      // Enrich recalls missing dealerPostalCode from selected_recalls (for existing routes
      // created before we stored postal code in route_data)
      const recallsNeedingPostalCode = routeItems.flatMap(route =>
        route.recalls
          .filter(r => !r.dealerPostalCode)
          .map(r => ({ vin: r.vin, campaignNumber: r.campaignNumber }))
      );
      if (recallsNeedingPostalCode.length > 0) {
        const vinsToLookup = [...new Set(recallsNeedingPostalCode.map(r => r.vin))];
        try {
          const prodClient = getProdSupabaseClient();
          const { data: selectedRecalls } = await prodClient
            .from('selected_recalls')
            .select('vin, campaign_number, dealer_postal_code')
            .in('vin', vinsToLookup);

          const postalCodeMap = new Map<string, string>();
          (selectedRecalls || []).forEach(
            (r: { vin: string; campaign_number: string; dealer_postal_code?: string }) => {
              if (r.dealer_postal_code) {
                postalCodeMap.set(`${r.vin}|${r.campaign_number}`, r.dealer_postal_code);
              }
            }
          );

          routeItems.forEach(route => {
            route.recalls.forEach(recall => {
              if (!recall.dealerPostalCode) {
                const key = `${recall.vin}|${recall.campaignNumber}`;
                const postalCode = postalCodeMap.get(key);
                if (postalCode) {
                  recall.dealerPostalCode = postalCode;
                }
              }
            });
          });
        } catch (enrichError) {
          // Non-fatal: continue without enrichment
          console.warn('[RouteTracker] Postal code enrichment failed:', enrichError);
        }
      }

      // Apply search filter
      let filtered = routeItems;
      if (filters.searchTerm) {
        const term = filters.searchTerm.toLowerCase();
        filtered = filtered.filter(
          item =>
            item.routeNumber.toLowerCase().includes(term) ||
            item.technicianName.toLowerCase().includes(term) ||
            item.recalls.some(r => r.vin.toLowerCase().includes(term))
        );
      }

      return filtered;
    } catch (error) {
      console.error('Error in getRoutesFromTable:', error);
      // Fall back to assignment-based approach
      return this.getRoutes(filters);
    }
  }
};
